import { useEffect, useRef } from 'react'

/**
 * Scroll-tracked vertical drift for vertical section labels.
 * Returns refs for a tall track element (spans the section) and the label
 * inside it. As the track travels through the viewport the label slides from
 * the track's top to its bottom — clamped, so it always starts and stops
 * with the section content. rAF-throttled, transform-only, disabled under
 * prefers-reduced-motion.
 */
export function useScrollDrift<T extends HTMLElement, U extends HTMLElement>() {
  const trackRef = useRef<T>(null)
  const labelRef = useRef<U>(null)

  useEffect(() => {
    const track = trackRef.current
    const label = labelRef.current
    if (!track || !label) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let raf = 0
    const update = () => {
      raf = 0
      const rect = track.getBoundingClientRect()
      const range = rect.height - label.offsetHeight
      if (range <= 0) {
        label.style.removeProperty('--drift-y')
        return
      }
      // Sticky-style: keep the label centered in the viewport while the
      // section is on screen, clamped so it starts at the section top and
      // stops at the section bottom (ogroup.com behavior).
      const centered = window.innerHeight / 2 - label.offsetHeight / 2 - rect.top
      const y = Math.min(range, Math.max(0, centered))
      // CSS var consumed by Tailwind's translate-y-[var(--drift-y,0px)] so the
      // composed transform keeps the label's rotate-180 (inline transform would clobber it)
      label.style.setProperty('--drift-y', `${y.toFixed(1)}px`)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  return { trackRef, labelRef }
}
